import toast from 'react-hot-toast'

export enum ToastIds {
  AgentUnavailable = 'agent-unavailable',
  IndexingRepositories = 'indexing-repositories'
}

interface ToastOptions {
  message: string
  id?: string
  duration?: number
  style?: Record<string, string>
  iconTheme?: Record<string, string>
  loading?: boolean
}

type ToastData = {
  toastId: string
  timerId?: number
}

const TOAST_STYLES = {
  success: {
    borderColor: 'rgb(34, 197, 94)',
    backgroundColor: 'rgb(240, 253, 244)',
    iconColor: 'rgb(34, 197, 94)',
    title: 'Success'
  },
  error: {
    borderColor: 'rgb(239, 68, 68)',
    backgroundColor: 'rgb(254, 242, 242)',
    iconColor: 'rgb(239, 68, 68)',
    title: 'Error'
  }
} as const

const defaultToastStyle = {
  maxWidth: '400px',
  border: '1px solid',
  borderRadius: '8px',
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  wordBreak: 'break-word'
} as const

export class BetterToast {
  private static _activeToasts = new Map<string, ToastData>()

  private static _toast(type: 'success' | 'error' | 'loading', args: ToastOptions | string) {
    let options: ToastOptions
    if (typeof args === 'string') {
      options = { message: args }
    } else {
      options = args
    }

    const { message, id, duration = 5000, style = {}, ...rest } = options
    const idToUse = id || message + Date.now()

    if (this._activeToasts.has(idToUse)) {
      const { toastId, timerId } = this._activeToasts.get(idToUse) as ToastData
      clearTimeout(timerId)
      const newTimerId = this._setDismissTimer(idToUse, duration)
      this._activeToasts.set(idToUse, { toastId, timerId: newTimerId })
      return idToUse
    }

    const styleConfig = TOAST_STYLES[type === 'loading' ? 'error' : type]

    const toastId = toast.custom(
      t => (
        <div
          className={`${t.visible ? 'animate-enter' : 'animate-leave'}`}
          style={{
            ...defaultToastStyle,
            borderColor: styleConfig.borderColor,
            backgroundColor: styleConfig.backgroundColor,
            ...style
          }}
        >
          <div className='flex items-center gap-3'>
            <div className='flex flex-col'>
              <span className='text-sm'>{message}</span>
            </div>
          </div>
        </div>
      ),
      {
        duration: duration === Infinity ? Infinity : duration,
        ...rest
      } as any
    )

    const timerId = this._setDismissTimer(idToUse, duration)
    this._activeToasts.set(idToUse, { toastId, timerId })
    return idToUse
  }

  private static _setDismissTimer(id: string, duration: number) {
    let timerId: number | undefined
    if (duration !== Infinity) {
      timerId = setTimeout(() => {
        BetterToast.dismiss(id)
      }, duration) as unknown as number
    }
    return timerId
  }

  public static success(options: ToastOptions | string) {
    return this._toast('success', options)
  }

  public static error(options: ToastOptions | string) {
    return this._toast('error', options)
  }

  public static stickyError(options: ToastOptions) {
    return this._toast(options.loading ? 'loading' : 'error', {
      ...options,
      duration: Infinity
    })
  }

  public static dismiss(id: string) {
    if (this._activeToasts.has(id)) {
      const { toastId, timerId } = this._activeToasts.get(id) as ToastData
      clearTimeout(timerId)
      toast.dismiss(toastId)
      this._activeToasts.delete(id)
    }
  }
}
